import { useCallback, useEffect, useState } from 'react'
import * as api from './api'
import { ApiError } from './api'
import { forgetCodeInUrl } from './routing'
import type { Invite } from './types'

/**
 * Where the guest is in the journey from "typed a code" to "looking at the
 * invite".
 *
 * `unlocking` exists purely so an arrival animation has somewhere to live: the
 * code has already been accepted and the invite is in hand, but the invite view
 * is not on screen yet. Whatever plays during that phase decides when it is
 * over by calling `completeUnlock`, so the animation is never cut short by the
 * network finishing first (or last).
 */
export type Phase = 'checking' | 'locked' | 'unlocking' | 'unlocked'

export interface Session {
  phase: Phase
  invite: Invite | null
  /** Set when a sign-in attempt failed; cleared on the next attempt. */
  signInError: string | null
  /**
   * A code that came in on the path and was turned down, so the prompt can open
   * with it already filled in for correcting.
   *
   * Null after a deliberate sign-out, so signing out does not leave somebody
   * else's code sitting in the box on a shared phone.
   */
  rejectedCode: string | null
  signingIn: boolean
  /** Exchanges a code for a session. Resolves true when the code was accepted. */
  signIn: (code: string) => Promise<boolean>
  /** Called by the unlock animation once it has finished playing. */
  completeUnlock: () => void
  /** Replaces the invite in state, e.g. after saving responses. */
  setInvite: (invite: Invite) => void
  signOut: () => Promise<void>
}

/**
 * @param codeFromLink a code taken off the URL, for the direct links printed on
 * invitations. It is put through the very same sign-in a typed code is.
 */
export function useSession(codeFromLink: string | null = null): Session {
  const [phase, setPhase] = useState<Phase>('checking')
  const [invite, setInviteState] = useState<Invite | null>(null)
  const [signInError, setSignInError] = useState<string | null>(null)
  const [rejectedCode, setRejectedCode] = useState<string | null>(null)
  const [signingIn, setSigningIn] = useState(false)

  useEffect(() => {
    let cancelled = false

    void (async () => {
      /*
       * A code on the path wins over any session already held.
       *
       * It has to: a household might open a link on a phone that is still signed
       * in as somebody else, and the link is the more recent statement of who is
       * looking.
       *
       * Whatever session is held goes first, before the new code is tried, so a
       * link that turns out to be wrong cannot leave the guest sitting in
       * somebody else's invite wondering why the names are strange. It is
       * unconditional because clearing a cookie that is not there costs nothing
       * and is simpler than asking whether one is.
       */
      if (codeFromLink !== null) {
        try {
          await api.deleteSession()
        } catch {
          // There may not have been one, and either way we are about to replace
          // it. Nothing here is worth reporting to the guest.
        }
        if (cancelled) return

        const accepted = await signIn(codeFromLink)
        if (cancelled) return
        if (accepted) {
          forgetCodeInUrl()
        } else {
          // Hand it to the prompt to be corrected. The URL keeps the code, so a
          // refresh reproduces this rather than silently forgetting it.
          setRejectedCode(codeFromLink)
          setPhase('locked')
        }
        return
      }

      // Otherwise pick up an existing session cookie, so a returning guest does
      // not have to dig their code out again.
      try {
        const existing = await api.getInvite()
        if (cancelled) return
        setInviteState(existing)
        // Straight to `unlocked`: a returning guest has not just typed a code,
        // so replaying the arrival animation would only be in their way.
        setPhase('unlocked')
      } catch (err) {
        if (cancelled) return
        // A 401 simply means "no session yet", which is not worth reporting.
        if (!(err instanceof ApiError && err.isUnauthenticated)) {
          setSignInError(errorMessage(err))
        }
        setPhase('locked')
      }
    })()

    return () => {
      cancelled = true
    }
    // signIn is stable; codeFromLink comes from the URL and does not change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeFromLink])

  const signIn = useCallback(async (code: string): Promise<boolean> => {
    setSigningIn(true)
    setSignInError(null)
    setRejectedCode(null)
    try {
      const found = await api.createSession(code)
      setInviteState(found)
      setPhase('unlocking')
      return true
    } catch (err) {
      setSignInError(errorMessage(err))
      return false
    } finally {
      setSigningIn(false)
    }
  }, [])

  const completeUnlock = useCallback(() => {
    setPhase((current) => (current === 'unlocking' ? 'unlocked' : current))
  }, [])

  const signOut = useCallback(async () => {
    try {
      await api.deleteSession()
    } catch {
      // Even if the call fails the guest asked to leave, so drop local state
      // and let them back in at the code prompt.
    }
    setInviteState(null)
    setSignInError(null)
    setRejectedCode(null)
    setPhase('locked')
  }, [])

  return {
    phase,
    invite,
    signInError,
    rejectedCode,
    signingIn,
    signIn,
    completeUnlock,
    setInvite: setInviteState,
    signOut,
  }
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message
  return 'Something went wrong. Please try again.'
}
